import { collection, doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { applyStayEntry } from '../utils/stayMutation.js';
import { calculateBill, roundMoney } from './billingService.js';
import { getBusinessDateKey } from '../utils/businessDate.js';
const clean = value => String(value ?? '').replace(/\s+/g, ' ').trim();
export const foodMenuCollection = (database, hotelId) => collection(database, 'hotelFoodMenus', hotelId, 'items');
const activeStay = stay => stay?.status === 'checked_in' && !stay.checkedOut && !stay.checkoutCompleted;
export const isActiveFoodStay = activeStay;
function audit(tx, reference, hotelId, actor, action, description, extra = {}) {
  if (!actor?.actorId) throw new Error('Manager login required hai.');
  tx.set(reference, {
    hotelId,
    hotelName: hotelId,
    ...actor,
    action,
    actionType: action,
    title: action.replaceAll('_', ' '),
    description,
    eventAt: new Date().toISOString(),
    createdAt: serverTimestamp(),
    ...extra
  });
}
export function validateMenuItem(input) {
  const price = Number(input.price);
  const name = clean(input.name),
    category = clean(input.category) || 'Food',
    unit = clean(input.unit);
  if (!name || name.length > 120 || category.length > 80 || unit.length > 80 || !Number.isFinite(price) || roundMoney(price) <= 0 || price > 1000000) {
    throw new Error('Item ka naam aur valid rate (₹0 se zyada) enter karein.');
  }
  return {
    name,
    category,
    unit,
    price: roundMoney(price),
    active: input.active !== false
  };
}
export async function saveFoodMenuItem({
  database,
  hotelId,
  itemId,
  previous = null,
  input,
  actor
}) {
  const reference = itemId ? doc(foodMenuCollection(database, hotelId), itemId) : doc(foodMenuCollection(database, hotelId));
  const log = doc(collection(database, 'auditLogs'));
  const fields = validateMenuItem(input);
  await runTransaction(database, async tx => {
    const snapshot = await tx.get(reference),
      current = snapshot.exists() ? snapshot.data() : null;
    if (previous ? !current || current.revision !== previous.revision : current) throw new Error('Menu item dusre device par change ho gaya. Fresh item khol kar save karein.');
    tx.set(reference, {
      ...fields,
      hotelId,
      revision: Number(current?.revision || 0) + 1,
      updatedAt: serverTimestamp()
    });
    audit(tx, log, hotelId, actor, 'food_menu_updated', `${fields.name}: ₹${fields.price}${fields.active ? '' : ' (disabled)'}`, {
      referenceId: reference.id,
      before: current || {},
      after: fields
    });
  });
  return reference.id;
}
// Item removal is a disable: historical bills and rate snapshots remain intact.
export async function placeFoodOrder({
  database,
  hotelId,
  stayId,
  expectedRoom,
  entry,
  actor,
  operationId
}) {
  const orderRef = operationId ? doc(database, 'foodOrders', operationId) : doc(collection(database, 'foodOrders'));
  const stayRef = doc(database, 'hotelStays', stayId),
    ledgerRef = doc(database, 'dayBookEntries', orderRef.id);
  const log = doc(database, 'auditLogs', `FOOD_${orderRef.id}`);
  const now = new Date().toISOString();
  return runTransaction(database, async tx => {
    const [staySnapshot, existing] = await Promise.all([tx.get(stayRef), tx.get(orderRef)]);
    if (!staySnapshot.exists()) throw new Error('Guest stay nahi mila.');
    const stay = staySnapshot.data();
    if (existing.exists()) {
      const old=existing.data();
      if (old.hotelId !== hotelId || old.stayId !== stayId) throw new Error('Order ID mismatch.');
      if(old.menuItemId!==entry.menuItemId || Number(old.quantity)!==Number(entry.quantity)
        || old.menuRevision!==entry.menuRevision || clean(old.note)!==clean(entry.note))
        throw new Error('Is order ID par pehle different item/quantity save ho chuki hai. Fresh order kholein.');
      return {
        ...stay,
        stayId
      };
    }
    if (stay.hotelId !== hotelId || !activeStay(stay) || clean(stay.room).toUpperCase() !== clean(expectedRoom).toUpperCase()) throw new Error('Guest/room change ho chuka hai. Fresh room kholein.');
    if (!entry.menuItemId || !Number.isSafeInteger(Number(entry.quantity)) || Number(entry.quantity) < 1 || Number(entry.quantity) > 999) throw new Error('Food item select karein; quantity 1–999 honi chahiye.');
    const menuSnapshot = await tx.get(doc(foodMenuCollection(database, hotelId), entry.menuItemId));
    if (!menuSnapshot.exists() || menuSnapshot.data().active === false) throw new Error('Yeh item menu mein available nahi hai.');
    const menu = menuSnapshot.data(),
      item = validateMenuItem(menu);
    if (menu.revision !== entry.menuRevision) throw new Error('Menu/rate update hua hai. Item dobara select karke order place karein.');
    const quantity = Number(entry.quantity),
      amount = roundMoney(item.price * quantity);
    const saved = {
      type: 'food',
      menuItemId: entry.menuItemId,
      menuRevision: menu.revision,
      title: item.name,
      category: item.category,
      unit: item.unit,
      quantity,
      rate: item.price,
      amount,
      note: clean(entry.note),
      source: 'food_menu',
      operationId: orderRef.id,
      orderId: orderRef.id,
      addedAt: now,
      receivedBy: actor.actorEmail || actor.actorName || 'Manager'
    };
    const next = applyStayEntry(stay, saved);
    tx.update(stayRef, {
      ...next,
      updatedAt: serverTimestamp()
    });
    tx.set(orderRef, {
      hotelId,
      stayId,
      room: stay.room,
      guestName: stay.guestName || '',
      ...saved,
      status: 'pending',
      revision: 1,
      createdBy: actor.actorId,
      createdByName: actor.actorName || 'Manager',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    const ledger = {
      hotelId,
      hotelName: stay.hotelName || hotelId,
      room: stay.room,
      guestName: stay.guestName || '',
      stayId,
      businessDate: getBusinessDateKey(new Date(now)),
      eventAt: now,
      hideFromRegister: true,
      operationId: orderRef.id,
      source: 'manager_pms',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      note: saved.note,
      paymentMode: '',
      transactionReference: ''
    };
    tx.set(ledgerRef, {
      ...ledger,
      entryType: 'revenue',
      category: 'food',
      title: item.name,
      amount
    });
    const before = calculateBill(stay),
      after = calculateBill(next);
    for (const [category, delta] of [['gst', roundMoney(after.gstAmount - before.gstAmount)], ['discount', roundMoney(after.discount - before.discount)]]) {
      if (delta !== 0) tx.set(doc(database, 'dayBookEntries', `${orderRef.id}_${category.toUpperCase()}`), {
        ...ledger,
        entryType: 'adjustment',
        category,
        title: `${category} adjustment`,
        amount: delta
      });
    }
    audit(tx, log, hotelId, actor, 'food_order_placed', `${quantity} × ${item.name} — pending delivery`, {
      room: stay.room,
      guestName: stay.guestName || '',
      stayId,
      referenceId: orderRef.id,
      amount
    });
    return {
      ...next,
      stayId
    };
  });
}
export async function changeFoodOrderStatus({
  database,
  hotelId,
  order,
  action,
  actor
}) {
  const reference = doc(database, 'foodOrders', order.id),
    log = doc(collection(database, 'auditLogs'));
  return runTransaction(database, async tx => {
    const snapshot = await tx.get(reference);
    if (!snapshot.exists() || snapshot.data().hotelId !== hotelId) throw new Error('Food order nahi mila.');
    const current = snapshot.data();
    const target = {
      done: 'done',
      undo: 'pending',
      delete: 'deleted'
    }[action];
    if (!target || current.status === 'deleted') throw new Error('Order history se remove ho chuka hai.');
    if (current.status === target) return;
    if (current.revision !== order.revision) throw new Error('Order dusre device par update hua. Fresh list se action karein.');
    const staySnapshot = await tx.get(doc(database, 'hotelStays', current.stayId));
    const stay = staySnapshot.exists() && staySnapshot.data().hotelId === hotelId ? staySnapshot.data() : null;
    if (action === 'done' && !activeStay(stay)) throw new Error('Guest checkout ho chuka hai. Delivery verify karein; pending list se Delete kar sakte hain.');
    if (action === 'undo' && !activeStay(stay)) throw new Error('Checkout ke baad order pending nahi kar sakte.');
    const room = stay?.room || current.room;
    tx.update(reference, {
      status: target,
      revision: current.revision + 1,
      updatedAt: serverTimestamp(),
      lastActionBy: actor.actorId,
      lastActionByName: actor.actorName || 'Manager',
      ...(action === 'done' ? {
        deliveredAt: new Date().toISOString(),
        deliveredBy: actor.actorId,
        deliveredByName: actor.actorName || 'Manager',
        deliveredRoom: room
      } : {})
    });
    audit(tx, log, hotelId, actor, `food_order_${action}`, `${current.title}: ${target}. Guest bill unchanged.`, {
      room,
      guestName: current.guestName,
      stayId: current.stayId,
      referenceId: reference.id,
      before: {
        status: current.status
      },
      after: {
        status: target
      }
    });
  });
}
export const ELVILLA_MENU = [['Beverages', 'Tea', 50], ['Beverages', 'Coffee', 70], ['Beverages', 'Milk', 80], ['Beverages', 'Cold Coffee', 120], ['Bread & Eggs', 'Butter Toast', 50, '2 pcs'], ['Bread & Eggs', 'Bread Jam', 100, '2 pcs'], ['Bread & Eggs', 'Omelette', 100], ['Bread & Eggs', 'Bread Omelette', 150], ['Bread & Eggs', 'Sandwich', 100, '2 pcs'], ['Bread & Eggs', 'Boiled Egg', 100, '2 pcs'], ['Bread & Eggs', 'Egg Fry', 150, '2 pcs'], ['Parathas', 'Aloo Paratha', 150, '2 pcs'], ['Parathas', 'Paneer Paratha', 200, '2 pcs'], ['Parathas', 'Onion Paratha', 150, '2 pcs'], ['Maggi', 'Plain Maggi', 120], ['Maggi', 'Vegetable Maggi', 150], ['Maggi', 'Cheese Maggi', 200], ['Maggi', 'Egg Maggi', 230], ['Snacks & Others', 'Peanut Masala', 150], ['Snacks & Others', 'Green Salad', 150], ['Snacks & Others', 'Poha', 120], ['Snacks & Others', 'Water Bottle', 30], ['Snacks & Others', 'Cold Drink', 60, '750 ml'], ['Snacks & Others', 'Lemon Water', 80], ['Snacks & Others', 'Soda', 50], ['Snacks & Others', 'Ice Cube', 60]].map(([category, name, price, unit = '']) => ({
  category,
  name,
  price,
  unit,
  active: true
}));
export const isElvillaHotel = name => /elvilla/i.test(name || '') && /safdarjung/i.test(name || '');
export async function loadElvillaMenu({
  database,
  hotelId,
  actor
}) {
  if (!isElvillaHotel(hotelId)) throw new Error('Yeh sample menu Elvilla Safdarjung ke liye hai.');
  const references = ELVILLA_MENU.map((_, i) => doc(foodMenuCollection(database, hotelId), `ELVILLA_${i + 1}`));
  const log = doc(collection(database, 'auditLogs'));
  await runTransaction(database, async tx => {
    const snapshots = await Promise.all(references.map(ref => tx.get(ref)));
    if (snapshots.some(snapshot => snapshot.exists())) throw new Error('Elvilla sample pehle load ho chuka hai. Existing items edit karein.');
    references.forEach((ref, i) => tx.set(ref, {
      ...ELVILLA_MENU[i],
      hotelId,
      revision: 1,
      updatedAt: serverTimestamp()
    }));
    audit(tx, log, hotelId, actor, 'food_menu_loaded', 'Elvilla sample menu loaded (26 items).');
  });
}
